import { useRef, useState, type FormEvent } from 'react';
import { messaging } from '@cwos/api-client';
import { relativeTime } from '@cwos/ui-shared';
import { errorMessage, senderLabel } from './messaging.js';

export interface SnippetSegment {
  text: string;
  hit: boolean;
}

export function snippetSegments(snippet: messaging.MessageSearchSnippet): SnippetSegment[] {
  const length = snippet.text.length;
  const ranges = snippet.highlights
    .map(({ start, length: highlightLength }) => {
      const from = Number.isFinite(start) ? Math.max(0, Math.trunc(start)) : 0;
      const to = Number.isFinite(highlightLength)
        ? Math.min(length, from + Math.max(0, Math.trunc(highlightLength)))
        : from;
      return { from, to };
    })
    .filter(({ from, to }) => to > from)
    .sort((a, b) => a.from - b.from || a.to - b.to)
    .reduce<Array<{ from: number; to: number }>>((merged, range) => {
      const previous = merged.at(-1);
      if (previous && range.from < previous.to) {
        previous.to = Math.max(previous.to, range.to);
      } else {
        merged.push(range);
      }
      return merged;
    }, []);

  const segments: SnippetSegment[] = [];
  if (snippet.truncatedStart) segments.push({ text: '…', hit: false });
  let cursor = 0;
  for (const range of ranges) {
    if (range.from > cursor)
      segments.push({ text: snippet.text.slice(cursor, range.from), hit: false });
    segments.push({ text: snippet.text.slice(range.from, range.to), hit: true });
    cursor = range.to;
  }
  if (cursor < length) segments.push({ text: snippet.text.slice(cursor), hit: false });
  if (snippet.truncatedEnd) segments.push({ text: '…', hit: false });
  return segments;
}

type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading'; previous: SearchState }
  | {
      kind: 'failed';
      error: unknown;
      query: string;
      cursor: string | null;
      append: boolean;
      previous: SearchState;
    }
  | {
      kind: 'done';
      query: string;
      results: messaging.MessageSearchResult[];
      nextCursor: string | null;
      hasMore: boolean;
    };

export function MessageSearch({
  channelId,
  onShow,
}: {
  channelId: string;
  onShow: (messageId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState<SearchState>({ kind: 'idle' });
  const requestToken = useRef(0);

  const execute = (rawQuery: string, cursor: string | null, append: boolean) => {
    const q = rawQuery.trim();
    if (q.length < 2) return;
    const token = ++requestToken.current;
    const previous = state.kind === 'failed' ? state.previous : state;
    setState({ kind: 'loading', previous });
    void messaging
      .searchMessages(channelId, { q, cursor: cursor ?? undefined, limit: 20 })
      .then((page) => {
        if (token !== requestToken.current) return;
        const baseResults = append && previous.kind === 'done' ? previous.results : [];
        setState({
          kind: 'done',
          query: q,
          results: append ? [...baseResults, ...page.results] : page.results,
          nextCursor: page.nextCursor,
          hasMore: page.hasMore,
        });
      })
      .catch((err: unknown) => {
        if (token !== requestToken.current) return;
        setState({ kind: 'failed', error: err, query: q, cursor, append, previous });
      });
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    execute(query, null, false);
  };
  const pending = state.kind === 'loading';
  const trimmed = query.trim();

  return (
    <div>
      <form role="search" onSubmit={submit}>
        <label className="msg-label" htmlFor="message-search-input">
          Search this conversation
        </label>
        <div style={{ display: 'flex', gap: 'var(--space-8)' }}>
          <input
            id="message-search-input"
            aria-label="Search this conversation"
            placeholder="Search this conversation"
            maxLength={200}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button type="submit" className="rn-link" disabled={pending || trimmed.length < 2}>
            Search
          </button>
        </div>
      </form>
      {trimmed.length < 2 ? <p className="rn-muted">Type at least two characters.</p> : null}
      {state.kind === 'loading' ? (
        <p role="status">Searching…</p>
      ) : state.kind === 'failed' ? (
        <>
          <p className="msg-error" role="status">
            {errorMessage(state.error, 'We couldn’t search this conversation just now.')}
          </p>
          <button
            type="button"
            className="rn-link"
            onClick={() => execute(state.query, state.cursor, state.append)}
          >
            Retry
          </button>
        </>
      ) : state.kind === 'done' ? (
        state.results.length === 0 ? (
          <p role="status">No messages in this conversation match “{state.query}”.</p>
        ) : (
          <>
            <ul aria-label="Search results">
              {state.results.map(({ message, snippet }) => {
                const when = relativeTime(new Date(message.createdAt));
                return (
                  <li key={message.id}>
                    <div>
                      <strong>{senderLabel(message)}</strong>{' '}
                      <time dateTime={message.createdAt}>{when.short}</time>
                    </div>
                    <p>
                      {snippetSegments(snippet).map((segment, index) =>
                        segment.hit ? (
                          <mark key={`${message.id}-${index}`}>{segment.text}</mark>
                        ) : (
                          <span key={`${message.id}-${index}`}>{segment.text}</span>
                        ),
                      )}
                    </p>
                    <button type="button" className="rn-link" onClick={() => onShow(message.id)}>
                      Show in conversation
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="rn-muted">
              Showing {state.results.length} matches
              {state.hasMore ? ' — more older matches' : ''}
            </p>
            {state.hasMore ? (
              <button
                type="button"
                className="rn-link"
                disabled={pending || state.nextCursor === null}
                onClick={() => execute(state.query, state.nextCursor, true)}
              >
                Older matches
              </button>
            ) : null}
          </>
        )
      ) : null}
    </div>
  );
}
